import React from 'react';

import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import LinearProgress, { LinearProgressProps } from '@mui/material/LinearProgress';

import { useGetJobByIdQuery } from 'services/busmanApi';

import { CustomTextField } from '../styles';

type StatusBoxProps = {
  status: string;
  title: string;
};

const StatusBox = ({ status, title }: StatusBoxProps) => {
  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="body2" gutterBottom>
        {title}
      </Typography>
      <CustomTextField
        InputProps={{
          readOnly: true,
        }}
        value={status}
        fullWidth
        name="type"
        size="small"
      />
    </Box>
  );
};

const EstimationStatus = (props: LinearProgressProps & { jobId: string }) => {
  const { data: job } = useGetJobByIdQuery(props.jobId);
  const theme = useTheme();

  const jobQoute = job && job.job_associated_quote;
  const jobQuotedAmount =
    Number(jobQoute?.quote_total_excluding_tax) - Number(jobQoute?.quote_inventory_total_excluding_tax);
  const quoteValue = Number(job && job.job_cost_projection) || 0;
  const jobProgress = jobQuotedAmount !== 0 ? (quoteValue / jobQuotedAmount) * 100 : 100;
  const exceeded = jobProgress > 100;

  return (
    <Box>
      <Typography variant="body2" gutterBottom>
        Project Progress
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
        <Box sx={{ width: '100%', mr: 1 }}>
          <LinearProgress
            variant="determinate"
            value={Math.min(jobProgress, 100)}
            sx={{
              height: 10,
              '& .MuiLinearProgress-bar': {
                backgroundColor:
                  jobProgress > 100 ? theme.palette.busmanColors.busmanRed : theme.palette.busmanColors.busmanPrimary,
              },
            }}
            {...props}
          />
        </Box>
        <Box
          className={exceeded ? 'shake exceeded-background' : ''}
          sx={{
            minWidth: 60,
          }}>
          <Typography
            variant="body2"
            sx={{
              color: exceeded ? theme.palette.busmanColors.busmanRed : theme.palette.busmanColors.busmanPrimary,
            }}>
            {`${Math.round(jobProgress)}%`}
          </Typography>

          {exceeded && (
            <Tooltip title="This job has exceeded the quoted budget.">
              <WarningAmberIcon
                sx={{
                  color: theme.palette.busmanColors.busmanRed,
                  fontSize: 18,
                }}
              />
            </Tooltip>
          )}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 2 }}>
        <StatusBox title={'Cost Projection'} status={`$${quoteValue}`} />
        <StatusBox title={'Quoted Amt'} status={`$${jobQuotedAmount}`} />
      </Box>
    </Box>
  );
};

export default EstimationStatus;
